import React, { createContext, useContext, useMemo } from 'react'
import { useSummaryConfig } from '../../hooks/summary/useSummaryConfig'
import { useSummaryDataProvider } from '../../api/hooks/summary/useSummaryDataProvider'
import { useSummaryChartOptions } from '../../hooks/summary'
import { useWorkspaceMetadata } from '../../api/hooks/workspaceMetadata/useWorkspaceMetadata'

const ExplorerContext = createContext(null)

/**
 * Provider for Explorer screen.
 * Provides full editing capabilities for building summaries.
 *
 * This context is NOT consumed by Summary component directly.
 * Instead, ExplorerSummary acts as a bridge, extracting props from context
 * and passing them to the dumb Summary component.
 *
 * This separation allows Summary to be reused in:
 * - Chat (with AI-generated config via useSummaryFromConfig)
 * - Canvas (with saved insights via useSummaryFromConfig)
 * - Slack embeds (with URL config via useSummaryFromConfig)
 */
export const ExplorerProvider = ({ children, initialConfig = null }) => {
    // Workspace metadata (tables, KPIs, filters, themes, etc.)
    const {
        workspaceMetadata,
        isLoading: workspaceLoading,
        refetch: refetchWorkspace,
        saveKPI,
        saveKPIPending,
        deleteKPI,
        deleteKPIPending,
        saveFilter,
        saveFilterPending,
        deleteFilter,
        saveTheme,
        isSaveThemeLoading,
        validateFormula,
        validateFormulaPending,
    } = useWorkspaceMetadata()

    // Config state with reducer
    const { state, actions, getMergedConfig } = useSummaryConfig(initialConfig, workspaceMetadata)

    // Data fetching - only refetches when dataConfig or queryConfig change
    // viewConfig affects API action type but is intentionally included
    const {
        summaryData,
        isLoading: dataLoading,
        isFetching,
        error,
        refetch,
    } = useSummaryDataProvider(
        state.dataConfig,
        state.queryConfig,
        state.viewConfig,
        state.queryConfig.activePrompts,
        { enabled: state.dataConfig.selectedItems.length > 0 }
    )

    // Chart options (memoized) - only rebuilds when summaryData or config changes
    // We need to pass a merged config because chart options need fields from both viewConfig and dataConfig
    const chartConfig = useMemo(
        () => ({
            ...state.viewConfig,
            ...state.dataConfig,
            sortColumns: state.queryConfig.sortColumns || [],
        }),
        [state.viewConfig, state.dataConfig, state.queryConfig.sortColumns]
    )
    const chartOptions = useSummaryChartOptions(summaryData, chartConfig)

    // Combined loading state
    const isLoading = workspaceLoading || dataLoading

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(
        () => ({
            // Workspace data
            workspaceMetadata,
            workspaceLoading,
            refetchWorkspace,

            // Workspace mutations
            saveKPI,
            saveKPIPending,
            deleteKPI,
            deleteKPIPending,
            saveFilter,
            saveFilterPending,
            deleteFilter,
            saveTheme,
            isSaveThemeLoading,
            validateFormula,
            validateFormulaPending,

            // Split configs (for fine-grained access)
            dataConfig: state.dataConfig,
            queryConfig: state.queryConfig,
            viewConfig: state.viewConfig,

            // Derived data
            summaryData,
            chartOptions,

            // Status
            isLoading,
            isFetching,
            error,
            hasData: summaryData?.series?.length > 0,
            hasSelectedItems: state.dataConfig.selectedItems.length > 0,

            // Actions (all editing capabilities)
            ...actions,
            refetch,
            getMergedConfig,
        }),
        [
            workspaceMetadata,
            workspaceLoading,
            refetchWorkspace,
            saveKPI,
            saveKPIPending,
            deleteKPI,
            deleteKPIPending,
            saveFilter,
            saveFilterPending,
            deleteFilter,
            saveTheme,
            isSaveThemeLoading,
            validateFormula,
            validateFormulaPending,
            state,
            summaryData,
            chartOptions,
            isLoading,
            isFetching,
            error,
            actions,
            refetch,
            getMergedConfig,
        ]
    )

    return <ExplorerContext.Provider value={value}>{children}</ExplorerContext.Provider>
}

/**
 * Hook to access Explorer context.
 * Must be used within ExplorerProvider.
 *
 * @returns {Object} - Explorer context value
 * @throws {Error} - If used outside of ExplorerProvider
 */
export const useExplorer = () => {
    const context = useContext(ExplorerContext)
    if (!context) {
        throw new Error('useExplorer must be used within ExplorerProvider')
    }
    return context
}

export { ExplorerContext }
